import React, { useCallback, useEffect, useRef, useState } from "react";
import { useConnectionState, useLocalParticipant } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import { Plus } from "lucide-react";
import {
  getLiveInterviewTranscript,
  saveLiveTranscriptSegment,
} from "../api/liveInterviewApi";
import {
  getDisplaySpeakerLabel,
  getParticipantRoleFromIdentity,
  getRoleSpeakerLabel,
  normalizeTranscriptSpeakerRole,
  parseParticipantMetadata,
} from "./liveTranscriptUtils";
import ApplicationContextPanel from "./ApplicationContextPanel";
import AiCompanionPanel from "./AiCompanionPanel";

// File-backed local meetings share one atomic store with transcript writers.
// Give a temporarily busy store enough time to release its lock, while polling
// at a measured cadence instead of repeatedly queueing refreshes behind it.
const TRANSCRIPT_REFRESH_TIMEOUT_MS = 15000;
const LIVE_TRANSCRIPT_REFRESH_MS = 2500;
const IDLE_TRANSCRIPT_REFRESH_MS = 5000;

const isTranscriptRequestAbort = (err) =>
  err?.name === "AbortError" || err?.code === "REQUEST_ABORTED";

const getWorkerStatusPresentation = (workerDiagnostics) => {
  if (!workerDiagnostics || typeof workerDiagnostics !== "object") {
    return "Diagnostics: Unavailable";
  }

  const workerStatus = String(workerDiagnostics.status || "").trim().toLowerCase();
  const tracks = Array.isArray(workerDiagnostics.tracks) ? workerDiagnostics.tracks : [];
  const failedTrack = tracks.find(
    (track) => String(track?.subscription_status || "").trim().toLowerCase() === "failed"
  );

  if (workerStatus === "active") {
    const failureCategory = String(failedTrack?.failure_category || "").trim().toLowerCase();
    if (failedTrack) {
      if (failureCategory === "worker_unavailable") {
        return "Worker: Active — Microphone subscription issue";
      }
      if (failureCategory === "transcript_save_failed") {
        return "Worker: Active — Transcript save issue";
      }
      return "Worker: Active — Track transcription issue";
    }
    if (tracks.some((track) => track?.last_successful_save_at)) {
      return "Worker: Active — Transcript saved";
    }
    if (tracks.some((track) => track?.last_final_at)) {
      return "Worker: Active — Final transcript received";
    }
    if (tracks.some((track) => track?.last_audio_received_at)) {
      return "Worker: Active — Receiving audio";
    }
    if (tracks.some((track) => String(track?.subscription_status || "").trim().toLowerCase() === "subscribed")) {
      return "Worker: Active — Microphone subscribed";
    }
    if (tracks.some((track) => String(track?.subscription_status || "").trim().toLowerCase() === "subscription_requested")) {
      return "Worker: Active — Subscribing to microphone";
    }
    return "Worker: Active";
  }
  if (workerStatus === "starting") return "Worker: Starting";
  if (workerStatus === "failed") return "Worker: Failed";
  if (workerStatus === "stopped") return "Worker: Unavailable";

  // `unavailable` is also returned when diagnostics belong to another backend
  // process, so it is not evidence that the worker itself failed.
  return "Diagnostics: Unavailable";
};

const TranscriptPanel = ({
  roomId,
  bookingId = "",
  speakerName,
  speakerRole,
  actorEmail = "",
  actorIdentity = "",
  actorRole = "",
  inviteToken = "",
  interviewerToken = "",
  readOnly = false,
  transcriptEnabled = true,
  canManageTranscript = false,
  onTranscriptEnabledChange,
  transcriptSettingBusy = false,
  transcriptSettingError = "",
  transcriptMutation = null,
  applicationContextProps = null,
  hideHeader = false,
}) => {
  const [segments, setSegments] = useState([]);
  const [manualText, setManualText] = useState("");
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [workerDiagnostics, setWorkerDiagnostics] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const loadInFlightRef = useRef(false);
  const activeRequestRef = useRef(null);
  const requestGenerationRef = useRef(0);
  const activeRoomIdRef = useRef("");
  const localMutationVersionRef = useRef(0);
  const localSegmentMutationVersionRef = useRef(new Map());
  const localSegmentSequenceRef = useRef(new Map());
  const isReadOnly = Boolean(readOnly);

  const connectionState = useConnectionState();
  const { localParticipant, isMicrophoneEnabled, microphoneTrack } = useLocalParticipant();
  const localParticipantMetadata = parseParticipantMetadata(localParticipant?.metadata);
  const participantIdentity = localParticipant?.identity || actorIdentity || "";
  const participantRole =
    normalizeTranscriptSpeakerRole(
      localParticipantMetadata.role ||
        actorRole ||
        speakerRole ||
        getParticipantRoleFromIdentity(participantIdentity)
    );
  const effectiveSpeakerRole = participantRole || "speaker";
  const resolvedSpeakerName =
    String(
      speakerName ||
      localParticipant?.name ||
      localParticipantMetadata.display_name ||
      participantIdentity ||
      actorIdentity ||
      "",
    ).trim() ||
    getRoleSpeakerLabel(effectiveSpeakerRole) ||
    "Speaker";
  const effectiveActorRole = String(actorRole || participantRole || speakerRole || "").trim().toLowerCase();
  const roomConnected = connectionState === ConnectionState.Connected;
  const isStaffViewer = effectiveActorRole !== "candidate";
  const hasAiCompanion = isStaffViewer && Boolean(applicationContextProps);
  const companionScopeKey = [
    String(roomId || "").trim(),
    String(bookingId || "").trim(),
    effectiveActorRole,
    String(actorIdentity || "").trim(),
    String(interviewerToken || "").trim(),
    hasAiCompanion ? "companion" : "transcript",
  ].join("::");
  const [activeDrawerSection, setActiveDrawerSection] = useState("transcript");

  useEffect(() => {
    setActiveDrawerSection(hasAiCompanion ? "companion" : "transcript");
  }, [companionScopeKey, hasAiCompanion]);

  const mergeSegments = useCallback((nextSegments) => {
    const byId = new Map();
    (nextSegments || []).forEach((segment, index) => {
      if (!segment) return;
      const key =
        segment.segment_id ||
        [
          segment.speaker_role || "",
          segment.transcript_text || "",
          segment.created_at || "",
          segment.client_timestamp || "",
          index,
        ].join("::");
      byId.set(key, segment);
    });
    return Array.from(byId.values()).sort((left, right) => {
      const leftTime = Date.parse(left?.created_at || left?.client_timestamp || "") || 0;
      const rightTime = Date.parse(right?.created_at || right?.client_timestamp || "") || 0;
      return leftTime - rightTime;
    });
  }, []);

  const mergeLocalSegment = useCallback(
    (currentSegments, segment) => {
      const segmentId = String(segment?.segment_id || "").trim();
      if (!segmentId) return currentSegments;
      const existing = (currentSegments || []).find(
        (currentSegment) => String(currentSegment?.segment_id || "").trim() === segmentId
      );
      if (existing?.is_final && segment?.is_final === false) {
        return currentSegments;
      }
      return mergeSegments([...(currentSegments || []), segment]);
    },
    [mergeSegments]
  );

  const applyLocalSegment = useCallback(
    (segment) => {
      const segmentId = String(segment?.segment_id || "").trim();
      if (!segmentId) return;
      const mutationVersion = localMutationVersionRef.current + 1;
      localMutationVersionRef.current = mutationVersion;
      localSegmentMutationVersionRef.current.set(segmentId, mutationVersion);
      setSegments((currentSegments) => mergeLocalSegment(currentSegments, segment));
    },
    [mergeLocalSegment]
  );

  const cancelActiveTranscriptRequest = useCallback(() => {
    const activeRequest = activeRequestRef.current;
    requestGenerationRef.current += 1;
    if (!activeRequest) return;

    if (activeRequest.timeoutId) {
      window.clearTimeout(activeRequest.timeoutId);
    }
    activeRequest.cancelled = true;
    activeRequest.controller.abort();
    activeRequestRef.current = null;
    loadInFlightRef.current = false;
  }, []);

  const loadTranscript = useCallback(
    async ({ silent = false } = {}) => {
      const safeRoomId = String(roomId || "").trim();
      if (!safeRoomId || loadInFlightRef.current) {
        if (!safeRoomId && !silent) {
          setSegments([]);
          setLoadingTranscript(false);
          setError("");
        }
        return;
      }

      const viewerRole = String(actorRole || speakerRole || "interviewer").trim().toLowerCase();
      const isStaffViewer = viewerRole !== "candidate";
      const controller = new AbortController();
      const generation = requestGenerationRef.current + 1;
      requestGenerationRef.current = generation;
      const activeRequest = {
        controller,
        generation,
        roomId: safeRoomId,
        timeoutId: null,
        timedOut: false,
        cancelled: false,
      };
      const localMutationVersionAtRequestStart = localMutationVersionRef.current;
      const isCurrentRequest = () =>
        activeRequestRef.current?.generation === generation &&
        !activeRequest.cancelled;

      activeRequestRef.current = activeRequest;
      loadInFlightRef.current = true;
      if (!silent) {
        setLoadingTranscript(true);
      }
      activeRequest.timeoutId = window.setTimeout(() => {
        if (!isCurrentRequest()) return;
        activeRequest.timedOut = true;
        controller.abort();
      }, TRANSCRIPT_REFRESH_TIMEOUT_MS);

      try {
        const includeAllRoomSegments = viewerRole !== "candidate";
        let payload = await getLiveInterviewTranscript(safeRoomId, {
          actorEmail,
          actorIdentity,
          role: actorRole || speakerRole,
          inviteToken: inviteToken || "",
          interviewerToken: interviewerToken || "",
          bookingId: includeAllRoomSegments ? undefined : bookingId || undefined,
          signal: controller.signal,
        });
        if (!isCurrentRequest()) return;
        let mergedSegments = mergeSegments(payload.segments || []);

        if (!mergedSegments.length && bookingId && !includeAllRoomSegments) {
          payload = await getLiveInterviewTranscript(safeRoomId, {
            actorEmail,
            actorIdentity,
            role: actorRole || speakerRole,
            inviteToken: inviteToken || "",
            interviewerToken: interviewerToken || "",
            signal: controller.signal,
          });
          if (!isCurrentRequest()) return;
          mergedSegments = mergeSegments(payload.segments || []);
        }

        if (!isCurrentRequest()) return;
        if (isStaffViewer) {
          const hasWorkerDiagnostics = Object.prototype.hasOwnProperty.call(
            payload || {},
            "worker_diagnostics"
          );
          setWorkerDiagnostics(
            hasWorkerDiagnostics && payload?.worker_diagnostics && typeof payload.worker_diagnostics === "object"
              ? payload.worker_diagnostics
              : null
          );
        }
        const localSegmentsUpdatedAfterRequestStarted = new Set(
          Array.from(localSegmentMutationVersionRef.current.entries())
            .filter(([, version]) => version > localMutationVersionAtRequestStart)
            .map(([segmentId]) => segmentId)
        );
        setSegments((currentSegments) => {
          if (!localSegmentsUpdatedAfterRequestStarted.size) {
            return mergedSegments;
          }
          const pendingLocalSegments = (currentSegments || []).filter((segment) =>
            localSegmentsUpdatedAfterRequestStarted.has(String(segment?.segment_id || "").trim())
          );
          return mergeSegments([...mergedSegments, ...pendingLocalSegments]);
        });
        localSegmentMutationVersionRef.current.forEach((version, segmentId) => {
          if (version <= localMutationVersionAtRequestStart) {
            localSegmentMutationVersionRef.current.delete(segmentId);
          }
        });
        setRefreshError("");
        if (!silent) {
          setError("");
        }
      } catch (err) {
        if (!isCurrentRequest()) return;
        const requestWasCancelled = isTranscriptRequestAbort(err);
        if (requestWasCancelled && !activeRequest.timedOut) {
          return;
        }
        const refreshMessage = activeRequest.timedOut
          ? "Live transcript refresh timed out. Retrying automatically."
          : "Live transcript updates are delayed. Retrying automatically.";
        if (isStaffViewer) {
          setRefreshError(refreshMessage);
        }
        if (!silent && !requestWasCancelled) {
          console.error("[LiveInterview] Transcript load failed", err);
          setError(err.message || "Failed to load transcript.");
        }
      } finally {
        if (isCurrentRequest()) {
          if (activeRequest.timeoutId) {
            window.clearTimeout(activeRequest.timeoutId);
          }
          activeRequestRef.current = null;
          loadInFlightRef.current = false;
          if (!silent) {
            setLoadingTranscript(false);
          }
        }
      }
    },
    [
      actorEmail,
      actorIdentity,
      actorRole,
      bookingId,
      interviewerToken,
      inviteToken,
      mergeSegments,
      roomId,
      speakerRole,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    const safeRoomId = String(roomId || "").trim();
    if (!safeRoomId) {
      cancelActiveTranscriptRequest();
      activeRoomIdRef.current = "";
      localMutationVersionRef.current = 0;
      localSegmentMutationVersionRef.current.clear();
      localSegmentSequenceRef.current.clear();
      setSegments([]);
      setLoadingTranscript(false);
      setError("");
      setRefreshError("");
      setWorkerDiagnostics(null);
      return undefined;
    }

    if (activeRoomIdRef.current !== safeRoomId) {
      activeRoomIdRef.current = safeRoomId;
      localMutationVersionRef.current = 0;
      localSegmentMutationVersionRef.current.clear();
      localSegmentSequenceRef.current.clear();
      setSegments([]);
      setRefreshError("");
      setWorkerDiagnostics(null);
    }

    const run = async (silent = false) => {
      if (cancelled) return;
      await loadTranscript({ silent });
    };

    void run(false);
    // A bounded live cadence keeps both participants responsive without
    // accumulating reads behind local file-store transcript writes. Final
    // segments are applied immediately through `transcriptMutation`.
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void run(true);
    }, roomConnected ? LIVE_TRANSCRIPT_REFRESH_MS : IDLE_TRANSCRIPT_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      cancelActiveTranscriptRequest();
    };
  }, [cancelActiveTranscriptRequest, loadTranscript, roomConnected, roomId]);

  useEffect(() => {
    const safeRoomId = String(roomId || "").trim();
    const mutationRoomId = String(transcriptMutation?.roomId || "").trim();
    const segment = transcriptMutation?.segment;
    const segmentId = String(segment?.segment_id || "").trim();
    if (!safeRoomId || mutationRoomId !== safeRoomId || !segmentId) return;

    const sequence = Number(transcriptMutation?.sequence);
    const lastSequence = localSegmentSequenceRef.current.get(segmentId) || 0;
    if (Number.isFinite(sequence) && sequence > 0) {
      if (sequence <= lastSequence) return;
      localSegmentSequenceRef.current.set(segmentId, sequence);
    }
    applyLocalSegment(segment);
  }, [applyLocalSegment, roomId, transcriptMutation]);

  const handleManualSave = async () => {
    setError("");
    if (isReadOnly) {
      setError("You do not have permission to edit the transcript.");
      return;
    }
    if (!transcriptEnabled) {
      setError("Transcript is turned off for this room.");
      return;
    }
    const safeRoomId = String(roomId || "").trim();
    if (!safeRoomId) {
      setError("Transcript cannot be saved until the room id is ready.");
      return;
    }

    try {
      const payload = await saveLiveTranscriptSegment(safeRoomId, {
        source: "browser",
        actor_email: actorEmail,
        actor_identity: actorIdentity || participantIdentity,
        role: effectiveSpeakerRole,
        invite_token: inviteToken,
        booking_id: bookingId || undefined,
        transcript_text: manualText.trim(),
        speaker_name: resolvedSpeakerName,
        speaker_role: effectiveSpeakerRole,
        speaker_identity: participantIdentity || actorIdentity,
        is_final: true,
      });
      applyLocalSegment(payload.segment);
      setManualText("");
    } catch (err) {
      setError(err.message || "Failed to save transcript.");
    }
  };

  const workerStatusLabel = getWorkerStatusPresentation(workerDiagnostics);
  const workerStatusIsRetained = Boolean(refreshError && workerDiagnostics);
  // This count is only a local trigger for the existing question API. The
  // server independently verifies worker provenance and applies the bounded
  // three-window policy before it can generate an adaptive batch.
  const verifiedWorkerFinalCount = segments.reduce(
    (count, segment) => (
      String(segment?.source || "").trim().toLowerCase() === "worker"
      && segment?.is_final === true
        ? count + 1
        : count
    ),
    0
  );

  return (
    <div className="live-interview-panel live-interview-transcript-panel-clean">
      {!hideHeader && !hasAiCompanion ? (
        <>
          <p className="live-interview-kicker">Transcript</p>
          <h2 className="live-interview-title" style={{ fontSize: 20 }}>
            Live notes and speech
          </h2>
        </>
      ) : null}

      {hasAiCompanion ? (
        <div className="live-interview-drawer-tabs" role="tablist" aria-label="Meeting assistant sections">
          <button
            type="button"
            id="live-interview-transcript-tab"
            className={`live-interview-drawer-tab${activeDrawerSection === "transcript" ? " is-active" : ""}`}
            role="tab"
            aria-selected={activeDrawerSection === "transcript"}
            aria-controls="live-interview-transcript-content"
            onClick={() => setActiveDrawerSection("transcript")}
          >
            Transcript
          </button>
          <button
            type="button"
            id="live-interview-ai-companion-tab"
            className={`live-interview-drawer-tab${activeDrawerSection === "companion" ? " is-active" : ""}`}
            role="tab"
            aria-selected={activeDrawerSection === "companion"}
            aria-controls="live-interview-ai-companion-content"
            onClick={() => setActiveDrawerSection("companion")}
          >
            AI Companion
          </button>
        </div>
      ) : null}

      <section
        id="live-interview-transcript-content"
        className="live-interview-drawer-section live-interview-transcript-content"
        role="tabpanel"
        aria-labelledby={hasAiCompanion ? "live-interview-transcript-tab" : undefined}
        hidden={hasAiCompanion && activeDrawerSection !== "transcript"}
      >
      {canManageTranscript ? (
        <div className="live-interview-transcript-status">
          <span>Transcript is {transcriptEnabled ? "ON" : "OFF"}</span>
          <button
            type="button"
            className="live-interview-btn"
            onClick={() => onTranscriptEnabledChange?.(!transcriptEnabled)}
            disabled={transcriptSettingBusy}
          >
            {transcriptSettingBusy ? "Saving..." : transcriptEnabled ? "Turn OFF" : "Turn ON"}
          </button>
        </div>
      ) : (
        <p className="live-interview-transcript-status-label">
          Transcript is {transcriptEnabled ? "ON" : "OFF"}
        </p>
      )}

      {effectiveActorRole === "candidate" ? (
        <p className="live-interview-transcript-consent">
          This interview may be transcribed for evaluation and quality purposes.
        </p>
      ) : null}

      {transcriptSettingError ? (
        <div className="live-interview-error" style={{ marginTop: 8 }}>
          {transcriptSettingError}
        </div>
      ) : null}

      {refreshError && effectiveActorRole !== "candidate" ? (
        <p className="live-interview-transcript-status-label" role="status" aria-live="polite">
          {refreshError}
        </p>
      ) : null}

      {effectiveActorRole !== "candidate" ? (
        <p className="live-interview-transcript-status-label" role="status" aria-live="polite">
          {workerStatusLabel}
          {workerStatusIsRetained ? " (last verified)" : ""}
        </p>
      ) : null}

      {!transcriptEnabled ? (
        <p className="live-interview-transcript-muted" style={{ marginTop: 8 }}>
          Transcript is off. Speech and notes are not being saved.
        </p>
      ) : null}

      <div className="live-interview-field live-interview-transcript-feed">
        <div className="live-interview-transcript-list" aria-live="polite">
          {loadingTranscript && segments.length === 0 ? (
            <div className="live-interview-empty">Loading transcript...</div>
          ) : segments.length === 0 ? (
            <div className="live-interview-empty">
              {transcriptEnabled
                ? "Waiting for speech. Lines appear here as they are captured."
                : "No transcript saved yet."}
            </div>
          ) : (
            segments.map((segment, index) => {
              // Interim (provisional) segments are rendered with a softer,
              // italic style so the host knows the text is still being
              // recognized and may change. When the matching final arrives,
              // the backend overwrites the same segment_id and the row
              // settles into the standard look.
              const isInterim = segment?.is_final === false;
              return (
                <div
                  className={`live-interview-segment${isInterim ? " is-interim" : ""}`}
                  key={segment.segment_id || `${segment.created_at || index}`}
                  data-interim={isInterim ? "true" : undefined}
                >
                  <strong>{getDisplaySpeakerLabel(segment, index)}</strong>
                  <p>
                    {segment.transcript_text || ""}
                    {isInterim ? <span className="live-interview-segment-cursor" aria-hidden>…</span> : null}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </div>

      {!isReadOnly ? (
        <div className="live-interview-transcript-manual">
          <div className="live-interview-field">
            <label>Add note</label>
            <textarea
              className="live-interview-textarea"
              value={manualText}
              onChange={(event) => setManualText(event.target.value)}
              placeholder="Type a note to add to the transcript"
              disabled={!transcriptEnabled}
            />
          </div>
          <button
            type="button"
            className="live-interview-btn"
            onClick={handleManualSave}
            disabled={!manualText.trim() || !transcriptEnabled}
          >
            <Plus size={16} />
            Save note
          </button>
        </div>
      ) : null}

      {error ? <div className="live-interview-error" style={{ marginTop: 12 }}>{error}</div> : null}
      </section>

      {hasAiCompanion ? (
        <section
          id="live-interview-ai-companion-content"
          className="live-interview-drawer-section live-interview-ai-companion-content"
          role="tabpanel"
          aria-labelledby="live-interview-ai-companion-tab"
          hidden={activeDrawerSection !== "companion"}
        >
          <ApplicationContextPanel
            {...applicationContextProps}
            renderReadyContent={({ applicationId }) => (
              <AiCompanionPanel
                roomId={roomId}
                bookingId={applicationContextProps.bookingId}
                applicationId={applicationId}
                actorRole={effectiveActorRole}
                interviewerToken={interviewerToken}
                verifiedWorkerFinalCount={verifiedWorkerFinalCount}
              />
            )}
          />
        </section>
      ) : null}
    </div>
  );
};

export default TranscriptPanel;
